import { useEffect, useCallback, useState } from "react";
import store from "store2";
import { useAccount, useSwitchNetwork, useNetwork } from "wagmi";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import { Provider, L1Signer, utils } from "zksync-web3";
import { zkSyncTestnet, zkSync } from "@wagmi/chains";
import { useContractWrite } from "wagmi";
import Escrow from "@/chain-info/contracts/Escrow.json";
import * as ethers from "ethers";
import stores, { actions, useTypedSelector } from "@/stores";
import useMintHistory from "@/hooks/useMintHistory";
import useMintType from "@/hooks/useMintType";
import ERC20 from "@/chain-info/contracts/MockERC20.json";
import mixpanel from "mixpanel-browser";
import { waitForTransaction, getWalletClient } from "wagmi/actions";
import { walletClientToSigner } from "@/services/ethers";
import { isSkipError } from "@/utils/validate";

import type { EscrowDeposit } from "@/types/network";
import { Token } from "@/types";

function isNativeToken(address?: string) {
  return (
    address === "0x0000000000000000000000000000000000000000" ||
    address === "0x0000000000000000000000000000000000001010"
  );
}

export default function useBridge(historyPrefix?: string) {
  const { form, changeFormField } = useVerifyClaim();

  const { history, status: historyStatus } = useMintHistory();
  const mintType = useMintType();

  const { connector } = useAccount();
  const { chain } = useNetwork();
  const { switchNetworkAsync } = useSwitchNetwork();

  const { depositing, depositSuccess } = useTypedSelector((state) => {
    return state.bridge;
  });

  const escrowDepositInfo = form?.karat as EscrowDeposit | undefined;
  const { writeAsync: depositNativeToken } = useContractWrite({
    address: escrowDepositInfo?.escrowContractAddress as any,
    abi: Escrow.abi,
    functionName: "depositNativeToken",
    chainId: escrowDepositInfo?.token?.chainId,

    value: isNativeToken(escrowDepositInfo?.token.tokenAddress)
      ? ethers.utils
          .parseEther(
            String(escrowDepositInfo?.tokenAmount?.toFixed(18) || "0")
          )
          .toBigInt()
      : 0n,
  });

  const { writeAsync: approveERC20 } = useContractWrite({
    address: escrowDepositInfo?.token.tokenAddress as any,
    abi: ERC20.abi,
    functionName: "approve",
    chainId: escrowDepositInfo?.token?.chainId,
    args: [
      escrowDepositInfo?.escrowContractAddress,
      ethers.utils.parseUnits(
        String(escrowDepositInfo?.tokenAmount?.toFixed(18) || "0"),
        escrowDepositInfo?.token.decimal
      ),
    ],
  });

  const karatDeposit = useCallback(async () => {
    const escrowDepositInfo = form?.karat as EscrowDeposit | undefined;
    console.log("start karat deposit", escrowDepositInfo);
    stores.dispatch(actions.bridge.setDepositing(true));
    try {
      if (!isNativeToken(escrowDepositInfo?.token.tokenAddress)) {
        const approveRes = await approveERC20();
        try {
          const approvetransReceipt = await waitForTransaction({
            hash: approveRes.hash,
          });
          if (approvetransReceipt.status === "reverted") {
            throw new Error("Approve fail");
          }
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }
      }
      const res = await depositNativeToken();
      const history = {
        transaction: res.hash,
        type: `${historyPrefix ? historyPrefix + "_" : ""}` + "karat",
        chainId: escrowDepositInfo?.token.chainId as number,
        value: parseFloat(String(escrowDepositInfo?.tokenAmount || "0")),
      };
      stores.dispatch(
        actions.mint.setHistory({
          history,
          type: mintType,
        })
      );
      try {
        const transReceipt = await waitForTransaction({ hash: res.hash });
        if (transReceipt.status === "reverted") {
          throw new Error("Deposit fail");
        }
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }

      console.log("karat deposit success");
      stores.dispatch(actions.bridge.setDepositSuccess(true));
      return history;
    } finally {
      stores.dispatch(actions.bridge.setDepositing(false));
    }
  }, [depositNativeToken, approveERC20, form, mintType]);

  const zkSyncDeposit = useCallback(async () => {
    const depositInfo = form?.zksync as {
      chain: string;
      depositCost: number;
    };

    if (!connector || !switchNetworkAsync) {
      return;
    }
    console.log("start zksync deposit", depositInfo);
    const chainId = parseInt(depositInfo.chain);
    if (chain?.id !== chainId) {
      await switchNetworkAsync(chainId);
    }
    mixpanel.track("zksync_deposit", {
      scene: historyPrefix,
    });
    const zksyncProvider = new Provider(
      chainId === 1
        ? "https://mainnet.era.zksync.io"
        : "https://testnet.era.zksync.dev"
    );
    const walletClient = await getWalletClient({
      chainId: chainId,
    });
    const signer = L1Signer.from(
      walletClientToSigner(walletClient),
      zksyncProvider
    );
    stores.dispatch(actions.bridge.setDepositing(true));
    try {
      const depositHandle = await signer.deposit({
        token: utils.ETH_ADDRESS,
        amount: ethers.utils.parseEther(String(depositInfo.depositCost)),
      });
      const history = {
        transaction: depositHandle.hash,
        type: `${historyPrefix ? historyPrefix + "_" : ""}` + "zksync",
        chainId,
        value: depositInfo.depositCost,
      };
      console.log("deposit history", history);
      stores.dispatch(
        actions.mint.setHistory({
          history,
          type: mintType,
        })
      );

      await depositHandle.wait();

      console.log("zksync deposit success");

      stores.dispatch(actions.bridge.setDepositSuccess(true));
      return history;
    } finally {
      stores.dispatch(actions.bridge.setDepositing(false));
    }
  }, [connector, switchNetworkAsync, form, chain, mintType, historyPrefix]);

  useEffect(() => {
    console.log("historyStatus", historyStatus);
    if (historyStatus === "success") {
      stores.dispatch(actions.bridge.setDepositSuccess(true));
      stores.dispatch(actions.bridge.setDepositing(false));
    } else if (historyStatus === "pending") {
      stores.dispatch(actions.bridge.setDepositing(true));
    } else {
      stores.dispatch(actions.bridge.setDepositSuccess(false));
      stores.dispatch(actions.bridge.setDepositing(false));
    }
  }, [historyStatus]);

  return {
    depositing,
    depositSuccess,
    karatDeposit,
    zkSyncDeposit,
    history,
  };
}
